import first from 'lodash/first';
import { createSelector } from 'reselect';

import { emptyArray } from 'Lib/constants/placeholder';
import type { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';

import type { Consent } from 'Store/consents/types';
import { AllInGender, AllInGenderGroup } from 'Store/profile/types';
import type { InterestedInGender, Photo, Photos } from 'Store/profile/user.types';

import type {
  AuthOnboardingValues,
  ErroredFields,
  FieldBooleanMap,
  SexualOrientation,
  ShowSameOrientationFirst,
} from './types';

// TODO: recycle these from some other constants file?
const MEDIUM = 2;
const LARGE = 1;

/**
 * Gets authOnboarding namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ReducerType} entities by type
 */
export const selectAuthOnboarding = createKeySelector('authOnboarding');

/**
 * Gets whether user is underage from top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if user submitted underage age
 */
export const getIsUnderage = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean | null => authOnboarding.isUnderage
);

/**
 * Gets whether user is reactivating their account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if user is reactivating an account
 */
export const getIsReactivated = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean => authOnboarding.isReactivated
);

/**
 * Gets flag where we know if the country is hostile or not.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingHasRedactedEnabled = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean => authOnboarding.hasRedactedEnabled ?? false
);

/**
 * Gets whether authOnboarding has been properly initialized
 *
 * @param  {GlobalStateType} state - top-level state
 * @return  true if onboarding is initialized
 */
export const getIsInitialized = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean => authOnboarding.isInitialized
);

/**
 * Gets whether authOnboarding has been properly completed
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if onboarding is completed
 */
export const getIsCompleted = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean => authOnboarding.isCompleted
);

/**
 * Gets whether in loading state during authOnboarding from top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if onboarding is in loading state
 */
export const getIsLoading = createSelector(
  selectAuthOnboarding,
  (authOnboarding): boolean => authOnboarding.isLoading
);

/**
 * Gets the auth onboarding steps
 *
 * @param  {GlobalStateType} state - top-level state
 * @return auth onboarding steps (by key)
 */
export const getOnboardingSteps = createSelector(
  selectAuthOnboarding,
  (authOnboarding): string[] => authOnboarding.steps
);

/**
 * Gets the current auth onboarding step
 *
 * @param  {GlobalStateType} state - top-level state
 * @return key of the current onboarding step
 */
export const getOnboardingCurrentStep = createSelector(
  selectAuthOnboarding,
  (authOnboarding): string | null => authOnboarding.currentStep
);

/**
 * Gets authOnboarding current step
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding step
 */
export const getOnboardingCurrentStepIndex = createSelector(
  [getOnboardingSteps, getOnboardingCurrentStep],
  (steps, currentStep): number => (currentStep ? steps.indexOf(currentStep) : -1)
);

/**
 * Gets the previous auth onboarding step
 *
 * @param  {GlobalStateType} state - top-level state
 * @return key of the current onboarding step
 */
export const getOnboardingPreviousStep = createSelector(
  [getOnboardingSteps, getOnboardingCurrentStepIndex],
  (steps, currentStepIndex): string | null =>
    currentStepIndex > 0 ? steps[currentStepIndex - 1] : null
);

/**
 * Gets the next auth onboarding step
 *
 * @param  {GlobalStateType} state - top-level state
 * @return key of the current onboarding step
 */
export const getOnboardingNextStep = createSelector(
  [getOnboardingSteps, getOnboardingCurrentStepIndex],
  (steps, currentStepIndex): string | null =>
    currentStepIndex < steps.length - 1 ? steps[currentStepIndex + 1] : null
);

/**
 * Gets whether authOnboarding has been started from top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return percent progress of onboarding
 */
export const getOnboardingProgress = createSelector(
  [getOnboardingSteps, getOnboardingCurrentStepIndex],
  (steps, currentStepIndex): number =>
    steps.length && currentStepIndex >= 0
      ? Math.round(100 * ((currentStepIndex + 1) / (steps.length + 1)))
      : 0
);

/**
 * Gets the initial values (since last API update) of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding initial values
 */
export const getOnboardingInitialValues = createSelector(
  selectAuthOnboarding,
  (authOnboarding): AuthOnboardingValues => authOnboarding.initialValues
);

/**
 * Gets the value of an onboarding field
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding initial value
 */
export const getOnboardingInitialValue = createSelector(
  [(_state: GlobalStateType, key: keyof AuthOnboardingValues) => key, getOnboardingInitialValues],
  (key, initialValues): any => initialValues[key]
);

/**
 * Gets the current values of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingValues = createSelector(
  selectAuthOnboarding,
  (authOnboarding): AuthOnboardingValues => authOnboarding.values
);

/**
 * Gets the value of an onboarding field
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field value
 */
export const getOnboardingValue = createSelector(
  [(_state: GlobalStateType, key: keyof AuthOnboardingValues) => key, getOnboardingValues],
  (key, values): any => values[key]
);

/**
 * Gets the current values of the onboarding showSameOrientationFirst
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingShowSameOrientationFirst = createSelector(
  getOnboardingValues,
  (authOnboarding): ShowSameOrientationFirst | null | undefined =>
    authOnboarding.showSameOrientationFirst
);

/**
 * Gets the current values of the onboarding showSameOrientationFirst
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingInterestedInGenders = createSelector(
  getOnboardingValues,
  (authOnboarding): InterestedInGender[] => authOnboarding.interestedInGenders || []
);

/**
 * Gets every all inclusive genders fetched from backend.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingAllInGender = createSelector(
  getOnboardingValues,
  (authOnboarding): AllInGenderGroup[] => authOnboarding.allInGender ?? []
);

/**
 * Gets selected all inclusive genders.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingSelectedAllInGender = createSelector(
  getOnboardingAllInGender,
  (allInGender): AllInGender[] => {
    const selectedGenders: AllInGender[] = [];
    allInGender.forEach((group) => {
      if (group.checked) {
        selectedGenders.push({ id: group.id, name: group.name, checked: true });
      }
      group.children?.forEach((child) => {
        if (child.checked) {
          selectedGenders.push({ id: child.id, name: child.name, checked: true });
        }
      });
    });
    return selectedGenders;
  }
);

/**
 * Gets selected all inclusive genders.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingGender = createSelector(
  getOnboardingValues,
  (authOnboarding): number | null => authOnboarding.gender
);

/**
 * Gets the current values of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingShowGenderOnProfile = createSelector(
  getOnboardingValues,
  (authOnboarding): boolean => authOnboarding.showGenderOnProfile
);

/**
 * Gets the current values of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingDisplayAllInGender = createSelector(
  getOnboardingValues,
  (authOnboarding): string[] => authOnboarding.displayGenders ?? []
);

/**
 * Gets all sexual orientations selected of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingSexualOrientation = createSelector(
  getOnboardingValues,
  (authOnboarding): SexualOrientation[] => authOnboarding.sexualOrientations ?? []
);

/**
 * Gets the current values of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingShowOrientationOnProfile = createSelector(
  getOnboardingValues,
  (authOnboarding): boolean => authOnboarding.showOrientationOnProfile
);

/**
 * Gets the current values of the onboarding fields
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field values
 */
export const getOnboardingDisplaySexualOrientations = createSelector(
  getOnboardingValues,
  (authOnboarding): string[] => authOnboarding.displaySexualOrientations ?? []
);

/**
 * Gets the mapping of onboarding fields to their required status
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding required field flags
 */
export const getOnboardingRequiredFields = createSelector(
  selectAuthOnboarding,
  (authOnboarding): FieldBooleanMap => authOnboarding.requiredFields
);

/**
 * Gets whether a field is required
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the field is required
 */
export const getOnboardingRequired = createSelector(
  [(_state: GlobalStateType, key: string) => key, getOnboardingRequiredFields],
  (key, requiredFields) => requiredFields[key] ?? false
);

/**
 * Gets the mapping of onboarding fields to their editable status
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding editable field flags
 */
export const getOnboardingEditableFields = createSelector(
  selectAuthOnboarding,
  (authOnboarding): FieldBooleanMap => authOnboarding.editableFields
);

/**
 * Gets whether an onboarding field is editable
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the field is editable
 */
export const getOnboardingEditable = createSelector(
  [(_state: GlobalStateType, key: keyof FieldBooleanMap) => key, getOnboardingEditableFields],
  (key, editableFields): boolean => editableFields[key] ?? false
);

/**
 * Gets authOnboarding errors from top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return map of authOnboarding errors
 */
export const getOnboardingErroredFields = createSelector(
  selectAuthOnboarding,
  (authOnboarding): ErroredFields => authOnboarding.erroredFields
);

/**
 * Gets the error for an onboarding field
 *
 * @param  {GlobalStateType} state - top-level state
 * @return onboarding field error
 */
export const getOnboardingError = createSelector(
  [(_state: GlobalStateType, key: keyof ErroredFields) => key, getOnboardingErroredFields],
  (key, erroredFields): string => erroredFields?.[key] || ''
);

/**
 * Gets the first onboarding photo from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return first photo in authOnboarding, if available.
 */
export const getOnboardingPhoto = createSelector(getOnboardingValues, (values): Photo | undefined =>
  first(values.photos)
);

/**
 * Gets large-sized onboarding photo
 *
 * @param  {GlobalStateType} state - top-level state
 * @return large-sized photo, if available
 */
export const getOnboardingPhotoMediumUrl = createSelector(
  getOnboardingPhoto,
  (photo): string | undefined => photo?.processedFiles?.[MEDIUM]?.url
);

/**
 * Gets large-sized onboarding photo
 *
 * @param  {GlobalStateType} state - top-level state
 * @return large-sized photo URL, if available
 */
export const getOnboardingPhotoLargeUrl = createSelector(
  getOnboardingPhoto,
  (photo): string | undefined => photo?.processedFiles?.[LARGE]?.url
);

/**
 * Returns the required consent
 *
 * @param  {GlobalStateType} state - top-level state
 * @return consent data containing sub-consents and title/body
 */
export const getOnboardingConsents = createSelector(
  [selectAuthOnboarding, getOnboardingValues],
  (authOnboarding, values): Consent | null => {
    const { consents } = values;
    const required = consents?.[0];
    if (!required) {
      return null;
    }
    return authOnboarding?.consents?.[required?.id];
  }
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return whether there is a disclaimer included in Tinder Rules or not
 */
export const showTinderRulesDisclaimer = createSelector(getOnboardingValues, (values): boolean =>
  (values?.tinderRules?.children || emptyArray).some((rule) => rule?.disclaimer)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return photos that are queued for upload post-login
 */
export const getQueuedPhotos = createSelector(
  selectAuthOnboarding,
  (authOnboarding): Photos => authOnboarding.queuedPhotos
);
